import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  CircularProgress,
  Button
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import { CSVLink } from "react-csv";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useTimezone } from "../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1)
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  filtersRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  searchField: {
    flex: 1,
    minWidth: 250
  },
  statusFilter: {
    minWidth: 200
  },
  exportButtons: {
    display: "flex",
    gap: theme.spacing(1)
  },
  tableContainer: {
    overflowX: "auto"
  },
  statusChipDelivered: {
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: 600
  },
  statusChipFailed: {
    backgroundColor: "#f44336",
    color: "white",
    fontWeight: 600
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4)
  }
}));

const ShippingDetailTable = ({ startDate, endDate, selectedCampaignId }) => {
  const classes = useStyles();
  const csvLinkRef = useRef(null);
  const { formatDateTime } = useTimezone();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadRecords = async () => {
      setLoading(true);
      try {
        const params = {
          page: page + 1,
          limit: rowsPerPage,
          status: filterStatus,
          search: searchTerm,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        };

        if (selectedCampaignId) {
          params.campaignId = selectedCampaignId;
        }

        const { data } = await api.get("/campaign-shipping-reports/detail", {
          params
        });

        if (isMounted) {
          setRecords(data.records || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error loading records:", err);
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRecords();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filterStatus, searchTerm, startDate, endDate, selectedCampaignId]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const prepareCSVData = () => {
    const data = records.map((record) => ({
      Campaña: record.campaign?.name || "N/A",
      Número: record.contact?.number || "N/A",
      Nombre: record.contact?.name || "N/A",
      Estado: record.deliveredAt ? "Entregado" : "No Entregado",
      "Fecha de Entrega": record.deliveredAt
        ? formatDateTime(record.deliveredAt) || "N/A"
        : "N/A",
      Mensaje: record.message || "N/A"
    }));

    setCsvData(data);

    // Trigger download
    setTimeout(() => {
      if (csvLinkRef.current) {
        csvLinkRef.current.link.click();
      }
    }, 100);
  };

  return (
    <Paper className={classes.panel}>
      <Typography variant="h6" className={classes.title}>
        <span role="img" aria-label="clipboard">📋</span> Detalle de Envíos
      </Typography>

      <Box className={classes.filtersRow}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          placeholder="Buscar por número o nombre..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />

        <TextField
          select
          className={classes.statusFilter}
          variant="outlined"
          size="small"
          label="Estado"
          value={filterStatus}
          onChange={handleFilterChange}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="delivered">Entregados</MenuItem>
          <MenuItem value="failed">No Entregados</MenuItem>
        </TextField>

        <Box className={classes.exportButtons}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={prepareCSVData}
            disabled={records.length === 0}
          >
            Exportar CSV
          </Button>
          <CSVLink
            data={csvData}
            filename={`informe-envios-${format(new Date(), "dd-MM-yyyy")}.csv`}
            ref={csvLinkRef}
            style={{ display: "none" }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box className={classes.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaña</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell>Fecha de Entrega</TableCell>
                  <TableCell>Mensaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No hay registros para mostrar
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{record.campaign?.name || "N/A"}</TableCell>
                      <TableCell>{record.contact?.number || "N/A"}</TableCell>
                      <TableCell>{record.contact?.name || "N/A"}</TableCell>
                      <TableCell align="center">
                        {record.deliveredAt ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Entregado"
                            size="small"
                            className={classes.statusChipDelivered}
                          />
                        ) : (
                          <Chip
                            icon={<CancelIcon />}
                            label="No Entregado"
                            size="small"
                            className={classes.statusChipFailed}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {record.deliveredAt
                          ? formatDateTime(record.deliveredAt) || "N/A"
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {record.message
                          ? record.message.length > 50
                            ? `${record.message.substring(0, 50)}...`
                            : record.message
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </>
      )}
    </Paper>
  );
};

export default ShippingDetailTable;

